import { looksLikeVideo } from "./utils";
import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link } from "wouter";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, stopProcessing, resetFFmpegEngine, convertVideoFormat } from "./operations";
import { VideoProcessingProgress, showVideoError, FileSizeNotice } from "./uiHelpers";
import { convertSummary, sameFormatNote, CONVERT_INPUT_NOTE } from "./convertSummary";
import { formatBytes } from "./utils";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Converts a video between MP4, web-optimized MP4 (fast-start for HTML5), WebM, and MOV. Prefer “MP4 for web” for sites; use WebM only when you specifically need a .webm file. Runs entirely in your browser — the video never leaves your device.`,
  how: `Upload your video, choose the format you want to convert it to, and download the converted file once processing finishes.`,
  tip: `For websites and embeds, choose “MP4 for web” (fast-start). WebM works in modern browsers but is slower to encode in-browser; use a short clip if it feels stuck. Because WebM is always re-encoded, short clips often come out larger than the original.`,
};

// WebP output is intentionally not offered here right now -- VP9/Opus
// (WebM's codecs) have a real, documented, unresolved crash in ffmpeg.wasm
// itself ("RuntimeError: memory access out of bounds", confirmed against
// multiple independent reports in the library's own GitHub issues, not
// something specific to this project or fixable with different encoding
// settings -- already tried faster presets and a full engine reset
// between operations, neither resolved it). Rather than delete the
// feature outright, the encoding logic below is left in place and simply
// unreachable via the UI, so it's ready to re-enable immediately if
// ffmpeg.wasm resolves this upstream, or to offer as a distinct AI-tier
// feature later if a different, more reliable encoding approach becomes
// available -- the same treatment Remove Background got earlier.
// WebM uses VP8 (not VP9) in operations.ts — VP9/Opus was hanging in ffmpeg.wasm.
const FORMAT_OPTIONS = [
  { value: "mp4", label: "MP4 (widely compatible)", mime: "video/mp4" },
  { value: "mp4-web", label: "MP4 for web (streaming / HTML5)", mime: "video/mp4" },
  { value: "webm", label: "WebM (HTML5 web video)", mime: "video/webm" },
  { value: "mov", label: "MOV (Apple / QuickTime)", mime: "video/quicktime" },
];

/** Output name; never identical to the input's, so the original is not overwritten in Downloads. */
function outName(name: string, ext: string): string {
  const base = name.replace(/\.[^.]+$/, "");
  const sameExt = (name.match(/\.([^.]+)$/)?.[1] ?? "").toLowerCase() === ext;
  return sameExt ? `${base}_converted.${ext}` : `${base}.${ext}`;
}

export default function ConvertVideo() {
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState("mp4");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const [summary, setSummary] = useState("");
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    if (!looksLikeVideo(f)) { toast.error(`"${f.name}" isn't a video file`); return; }
    setFile(f); setResult(null); setSummary("");
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) stopProcessing(ffmpegRef.current);
  };

  const convert = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading video engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading video file...");
      const inExt = file.name.match(/\.([^.]+)$/)?.[1] || "mp4";
      await ffmpeg.writeFile(`input.${inExt}`, await fetchFile(file));

      setStatusMessage(`Converting to ${format.toUpperCase()}...`);
      const outExt = format === "mp4-web" ? "mp4" : format;
      const { data, rewrapped } = await convertVideoFormat(ffmpeg, `input.${inExt}`, `output.${outExt}`, format, setProgress, controller.signal);

      const mime = FORMAT_OPTIONS.find(f => f.value === format)?.mime || "video/mp4";
      const blob = ffmpegOutputToBlob(data, mime);
      setResult(blob);
      setSummary(convertSummary({ fileName: file.name, inBytes: file.size, outBytes: blob.size, format, rewrapped }));
      toast.success(rewrapped ? `Converted in seconds without re-encoding (no quality loss): ${formatBytes(blob.size)}` : `Video converted: ${formatBytes(blob.size)}`);
    } catch (err) {
      showVideoError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="video/*" maxSize={5000} onFiles={handleFiles} label="Drop a video to convert" description={`Works with ${CONVERT_INPUT_NOTE}. Output: MP4, MP4 for web, WebM or MOV.`} />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          <div><Label>Output Format</Label>
            <Select value={format} onValueChange={(v) => { setFormat(v); setResult(null); }}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORMAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {sameFormatNote(file.name, format) && <p className="text-xs text-muted-foreground mt-1" data-testid="same-format-note">{sameFormatNote(file.name, format)}</p>}
            {format === "webm" && <p className="text-xs text-muted-foreground mt-1" data-testid="webm-note">Short clips often get bigger as WebM, because the video is re-encoded rather than just repackaged.</p>}
          </div>
          {processing && <VideoProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && summary && <p className="text-sm" data-testid="convert-summary">{summary}</p>}
          {result && <ResultPreview blob={result} />}
          {result && <p className="text-xs text-muted-foreground">Want only the sound? <Link href="/tool/extract-audio" className="underline">Extract Audio</Link> saves it as MP3, WAV, M4A, OGG or FLAC.</p>}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={outName(file.name, format === "mp4-web" ? "mp4" : format)} />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={convert} disabled={processing || sizeCheck?.severity !== "ok"}>{processing ? "Converting..." : "Convert"}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
